"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useTranslations } from "next-intl";
import { englishTaxonomyFormatter } from "@/utils/englishTaxonomy";
import { createTaxonomyFormatter } from "@/utils/formatTaxonomy";
import { createTextFilter, matchText } from "@/utils/textMatch";
import { CheckIcon } from "lucide-react";
import Image from "next/image";
import type * as React from "react";

/**
 * cmdk filter for the activity picker: matches each item's localized and English
 * names (supplied as keywords below), so searches work in either language.
 */
export const activityTypeTextFilter = createTextFilter({
  matcher: matchText,
  matchAliases: true,
});

export type ActivityOption = {
  value: string;
  count?: number;
};

export type CleanActivitySelectProps = {
  items: ActivityOption[];
  value: string[];
  onChange: (next: string[]) => void;
  showOnlySelected?: boolean;
  withSearch?: boolean;
  allLabel?: string;
  listOnly?: boolean;
};

export default function ActivitySelect({
  items,
  value,
  onChange,
  withSearch = true,
  allLabel,
  listOnly = false,
}: CleanActivitySelectProps) {
  const t = useTranslations();
  const format = createTaxonomyFormatter(t);

  const isAllSelected = value.length === 0;
  const selectedActivity = value.length > 0 ? value[0] : null;

  // Search keywords for an activity: its localized name plus the English name
  // (deduped when identical). We always supply both; the search filter decides
  // whether the English alias actually counts.
  const activityKeywords = (activityType: string): string[] => {
    const localized = format.activity(activityType);
    const english = englishTaxonomyFormatter.activity(activityType);
    return english === localized ? [localized] : [localized, english];
  };

  const list = (
    <CommandList
      className="sm:mt-2"
      style={{ "--cmdk-list-height": "100%" } as React.CSSProperties}
    >
      <CommandEmpty>{t("activitiesDropdown.empty")}</CommandEmpty>

      {/* Selected section */}
      <CommandGroup heading={t("activitiesDropdown.selected")}>
        <CommandItem
          key={selectedActivity ?? "__all__"}
          value={selectedActivity ?? "__all__"}
          keywords={
            selectedActivity
              ? activityKeywords(selectedActivity)
              : [allLabel ?? t("activitiesDropdown.all")]
          }
          onSelect={() => onChange([])}
          className="flex items-center gap-3 px-2 py-3 bg-transparent data-[selected=true]:bg-transparent"
        >
          <div className="shrink-0 size-10 rounded-sm overflow-clip">
            <Image
              src="/search/search-activity.png"
              alt=""
              width={40}
              height={40}
              className="rounded-sm object-cover"
              sizes="40px"
            />
          </div>
          <span className="flex-1 min-w-0 text-base font-medium leading-6 truncate">
            {selectedActivity
              ? format.activity(selectedActivity)
              : (allLabel ?? t("activitiesDropdown.all"))}
          </span>
          <CheckIcon className="size-5 shrink-0 text-foreground" />
        </CommandItem>
      </CommandGroup>

      {/* All activities section */}
      <CommandGroup heading={t("activitiesDropdown.all")}>
        {/* Show "All activities" option only if an activity is currently selected */}
        {!isAllSelected && (
          <CommandItem
            key="__all__"
            value="__all__"
            keywords={[allLabel ?? t("activitiesDropdown.all")]}
            onSelect={() => onChange([])}
            className="flex items-center gap-3 px-2 py-3"
          >
            <span className="flex-1 min-w-0 text-base leading-6 truncate">
              {allLabel ?? t("activitiesDropdown.all")}
            </span>
          </CommandItem>
        )}
        {items.map(({ value: v }) => {
          const isSelected = value.includes(v);
          // Skip rendering if this is the selected activity (shown in Selected section)
          if (isSelected) return null;
          return (
            <CommandItem
              key={v}
              value={v}
              keywords={activityKeywords(v)}
              onSelect={() => onChange([v])}
              className="flex items-center gap-3 px-2 py-3"
            >
              <span className="flex-1 min-w-0 text-base leading-6 truncate">
                {format.activity(v)}
              </span>
            </CommandItem>
          );
        })}
      </CommandGroup>
    </CommandList>
  );

  if (listOnly) {
    return <div className="flex flex-col">{list}</div>;
  }

  return (
    <Command className="flex flex-col" filter={activityTypeTextFilter}>
      {withSearch && (
        <CommandInput
          placeholder={t("activitiesDropdown.searchPlaceholder")}
          autoFocus={true}
        />
      )}
      {list}
    </Command>
  );
}
